import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { fetchCustomers, fetchTenants } from '../api/authApi';
import { PageHeader } from '../components/PageHeader';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { TableToolbar } from '../components/TableToolbar';
import { SectionCard } from '../components/SectionCard';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { StatusBadge } from '../components/StatusBadge';
import { IconButton } from '../components/ui/IconButton';
import { TextInput } from '../components/ui/TextInput';

const TENANT_STATUSES = ['active', 'trial', 'suspended', 'provisioning'];

export function TenantsPage() {
  const { t } = useTranslation(['tenants', 'common', 'statuses']);
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [customerId, setCustomerId] = useState('');

  useEffect(() => {
    fetchCustomers({ per_page: 100 })
      .then((response) => setCustomers(response.data || []))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const params = {};
    if (search.trim()) {
      params.search = search.trim();
    }
    if (status) {
      params.status = status;
    }
    if (customerId) {
      params.customer_id = customerId;
    }

    fetchTenants(params)
      .then((response) => setTenants(response.data || []))
      .catch(() => setError(t('tenants:load_failed')))
      .finally(() => setLoading(false));
  }, [search, status, customerId, t]);

  const emptyValue = t('common:empty_value');
  const recordCount = tenants.length;

  const columns = useMemo(
    () => [
      {
        id: 'id',
        label: t('common:id'),
        width: 72,
        render: (row) => row.id ?? emptyValue,
      },
      {
        id: 'name',
        label: t('common:name'),
        render: (row) => row.name || emptyValue,
      },
      {
        id: 'slug',
        label: t('common:slug'),
        render: (row) => row.slug || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? <StatusBadge status={row.status} category="tenant" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'customer',
        label: t('common:customer'),
        render: (row) => row.customer?.name ?? row.customer_id ?? emptyValue,
      },
      {
        id: 'access',
        label: t('common:access'),
        render: (row) => (
          <StatusBadge
            status={row.is_access_allowed ? 'allowed' : 'blocked'}
            apiLabel={
              row.is_access_allowed
                ? t('statuses:access.allowed')
                : t('statuses:access.blocked')
            }
            size="Sm"
            tone={row.is_access_allowed ? 'Success' : 'Danger'}
          />
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/tenants/${row.id}`);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  return (
    <Box>
      <PageHeader
        type="List"
        title={t('tenants:title')}
        subtitle={t('tenants:list_subtitle')}
      />

      <FilterBar type="Simple">
        <TextInput
          density="Compact"
          hideLabel
          label={t('common:search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {TENANT_STATUSES.map((value) => (
            <FilterSelectOption key={value} value={value}>
              {t(`statuses:tenant.${value}`)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('common:customer')}
          hideLabel
          value={customerId}
          onChange={(event) => setCustomerId(event.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {customers.map((customer) => (
            <FilterSelectOption key={customer.id} value={String(customer.id)}>
              {customer.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding>
        <TableToolbar
          mode="Default"
          title={t('tenants:title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('tenants:toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={tenants}
            emptyMessage={t('tenants:empty_list')}
            onRowClick={(row) => navigate(`/tenants/${row.id}`)}
          />
        )}
      </SectionCard>
    </Box>
  );
}
